import { NgModule, ErrorHandler } from "@angular/core";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app/app.component";
import { environment } from "../environments/environment";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { StoreModule } from "@ngrx/store";
import { EffectsModule } from "@ngrx/effects";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { reducers, metaReducers, HttpErrorInterceptor } from "./core";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { SettingsService } from "./core/settings";
import { BrowserModule } from "@angular/platform-browser";
import { LightstoneOnboardingModule } from "@lightstone/onboarding";
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from "@azure/msal-browser";
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
  MsalGuardAuthRequest,
} from "@azure/msal-angular";

import { DownloadServiceFactory } from "./core/download/download.factory";
import { ErrorHandlerService } from "./core/monitoring/errorHandler.service";
import { PDFDownloadProgressComponent } from "./core/download/pdf/pdf-download-progress-component";
import { WhatsAppShareComponent } from "./core/download/pdf/whatsapp-share/whatsapp-share.component";
import { AcceptInviteComponent } from "./partials/accept-invite/accept-invite.component";
import { NgxPermissionsModule } from "ngx-permissions";
import { ReferralModule } from "src/referral/referral.module";
import { OnsellProgressComponent } from "./core/product/onsell/onsell-progress.component";
import { EventService } from "@lightstone/config";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { ProductEffects } from "./core/store/product/_effects";
import { SignupDoneComponent } from "./pages/signupdone/signupdone.component";
import { B2cConfig } from "./core/auth/b2c-config";
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

const isIE =
  window.navigator.userAgent.indexOf("MSIE ") > -1 ||
  window.navigator.userAgent.indexOf("Trident/") > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.production
        ? window["jupiter"].clientID
        : environment.clientID,
      authority: B2cConfig.authority.login,
      knownAuthorities: ["login.lightstone.co.za"],
      redirectUri: window["jupiter"].redirectUri,
      postLogoutRedirectUri: window["jupiter"].logoutRedirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  if (!localStorage.getItem("InitialLoadUrl")) {
    const hash = window.location.hash;
    const isMsalResponse = hash.includes("state=") && hash.includes("code=");
    if (hash && hash !== "#/" && hash !== "#" && !isMsalResponse) {
      localStorage.setItem("InitialLoadUrl", hash.substring(1));
    }
  }

  let request = null as MsalGuardAuthRequest;
  let url = window.location.href.toLowerCase();
  if (url.indexOf("email=") != -1) {
    let email = url.split("email=")[1].split("&")[0];
    request = {
      loginHint: email,
    };
  } else {
    const hintEmail = localStorage.getItem("hint_email");
    const signedinEmail = localStorage.getItem("signedinEmail");
    if (hintEmail || signedinEmail) {
      request = {
        loginHint: hintEmail ?? signedinEmail,
      };
    }
  }

  return {
    interactionType: InteractionType.Redirect,
    authRequest: request,
  };
}

@NgModule({
  declarations: [
    AppComponent,
    PDFDownloadProgressComponent,
    WhatsAppShareComponent,
    OnsellProgressComponent,
    AcceptInviteComponent,
    SignupDoneComponent,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    MsalModule,
    NgbModule,
    FontAwesomeModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot([ProductEffects]),
    StoreRouterConnectingModule.forRoot({ stateKey: "router" }),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    LightstoneOnboardingModule,
    NgxPermissionsModule.forRoot(),
    ReferralModule,
    MsalModule,
    NgxIntlTelInputModule
  ],
  providers: [
    provideAnimations(),
    MsalService,
    {
      provide: "SettingsModel",
      useFactory: (settingsService: SettingsService) => {
        return settingsService.getSettings();
      },
      deps: [SettingsService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: DownloadServiceFactory,
      useClass: DownloadServiceFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    {
      provide: PDFDownloadProgressComponent,
      useClass: PDFDownloadProgressComponent,
    },
    MsalGuard,
    MsalBroadcastService,
    EventService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
